import { track } from '@src/services/analytics';
import React from 'react';
import styled from 'styled-components';
import { formatCents } from '@src/common';
import { device } from '@src/common/breakpoints';
import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { changeLocation } from '@src/core/store';
import { useWidgetsTheme as theme } from '@src/core/theme';
import { completeCartSelector } from '@src/features/cart';
import { appInfoSelector } from '@src/features/auth';
import { BodyLargeBold, BodyRegular, BodyRegularMedium, Heading5 } from '@src/fonts/typography/Typography';
import { DarkTextColor } from '@src/fonts/typography/TypographyColors';
import useTranslate, { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import GradientButton from '@src/components/reusable/button/GradientButton';
import CartCouponArea from './CartCouponArea';
import ErrorWarning from './ErrorWarning';
import { AlertContainer, AlertContent } from '@src/components/reusable/Alert/style';
import { snakeCase } from 'lodash';

type CartCheckoutProps = {
  enableCoupon?: boolean;
  onCheckout?: () => void;
  proceedWithErrors?: boolean;
};

const CartCheckout = (props: CartCheckoutProps) => {
  const { enableCoupon = true, onCheckout, proceedWithErrors = false } = props;

  const actionColor = theme('actionColor');
  const checkoutPage = useWidgetParam('checkout-page');
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const { cart, hasErrors } = useAppSelector(completeCartSelector);
  const appInfo = useAppSelector(appInfoSelector);

  const onCheckoutButtonClick = () => {
    track('cartCheckoutClick');

    if (onCheckout) {
      onCheckout();
    } else {
      dispatch(changeLocation(checkoutPage ?? ''));
    }
  };

  const fromValue = cart?.costPreview.fullPartialAmountCents ?? 0;
  const toValue = cart?.costPreview.totalAmountCents ?? 0;
  const difference = fromValue - toValue;
  const discount = (100 * difference) / fromValue;
  const generalErrors = cart?.generalErrors;

  return (
    <CartCheckoutContainer>
      {enableCoupon && appInfo?.hasCouponAvailable ? (
        <>
          <CartCouponArea />

          <HorizontalBar />
        </>
      ) : null}

      {cart?.coupon?.invalid ? (
        <div>
          <ErrorWarning>{i18n('checkout_invalidCoupon')}</ErrorWarning>
        </div>
      ) : null}

      {discount ? (
        <CartCheckoutInfo>
          <TotalWithoutDiscountHeader>{i18n('cart_subtotal')}</TotalWithoutDiscountHeader>
          <TotalWithoutDiscountValue>{formatCents(fromValue)}</TotalWithoutDiscountValue>
        </CartCheckoutInfo>
      ) : null}

      <CartCheckoutInfo>
        <TotalHeader>{i18n('cart_totalLabel')}</TotalHeader>
        <TotalValue>{formatCents(toValue)}</TotalValue>
      </CartCheckoutInfo>

      {generalErrors && generalErrors.length ? (
        <AlertContainer severity='error'>
          <AlertContent>
            {generalErrors.map((error) => (
              <span key={error.type}>{i18n(snakeCase(error.type) as unknown as DefaultMessagesKeys)}</span>
            ))}
          </AlertContent>
        </AlertContainer>
      ) : null}

      <ButtonArea>
        <GradientButton
          onClick={onCheckoutButtonClick}
          disabled={!proceedWithErrors && hasErrors}
          actionColor={actionColor}
        >
          {i18n('cart_checkout')}
        </GradientButton>
      </ButtonArea>
    </CartCheckoutContainer>
  );
};

const ButtonArea = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;

  ${device.mobile} {
    margin-bottom: 0px;
  }
`;

const TotalHeader = styled.p`
  ${BodyLargeBold}
  ${DarkTextColor}

  ${device.mobile} {
    ${Heading5}
  }
`;
const TotalValue = styled.p`
  ${BodyLargeBold}
  ${DarkTextColor}

  ${device.mobile} {
    ${Heading5}
  }
`;
const HorizontalBar = styled.hr`
  border-top: 1px solid #cdcdcd;
  margin-top: 0px;
  margin-bottom: 16px;
  width: 100%;
`;
const CartCheckoutInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 4px 0;
`;
const CartCheckoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TotalWithoutDiscountHeader = styled.p`
  ${BodyRegular}
`;

const TotalWithoutDiscountValue = styled.p`
  ${BodyRegularMedium}
  border-radius: 4px;
  background: #eee;
  padding: 2px 6px;
  text-decoration: line-through;
`;

export default CartCheckout;
